import React from 'react';

import { BlockProvider } from 'components/shared/BlockProvider';
import { PAGE_TYPE_PRE_TRAVEL_INFO } from 'constants/index';
import useMediaBreakpoints from 'hooks/useMediaBreakpoints';
import { debounce } from 'utils/debounce';
import { isFalse, isTrue } from 'utils/ramda';

import BlockWrapper from '../../blocks/BlockWrapper';
import { BLOCKS_CONFIG } from '../../blocks/configs';
import { IGetBlockPropsResult } from '../../blocks/types';
import LandingPageContext from '../../contexts/LandingPageContext';
import * as types from './types';

function LandingBlock({
  block,
  blockId,
  blockIndex,
  visibleBlockIndex,
  isNextBlocked,
  firstBlockerRendered,
  pageHeadlineRef,
  blockerRef,
  blockActions,
  typeContextProps,
  isInsideAccordion = false,
}: types.Props) {
  const { isTabletOrMobile } = useMediaBreakpoints();

  const {
    landingData,
    hasHeader,
    pageIndex = 0,
    isItinerariesItem = false,
    isPortal = false,
    isPortalItinerary = false,
    links,
  } = React.useContext(LandingPageContext);

  const isPreTravelInfo = landingData?.type === PAGE_TYPE_PRE_TRAVEL_INFO;
  const isMobileNavHidden = isTabletOrMobile && links?.length < 2;

  const { disableFirstSectionNameRemoval, blockAddingAvailable } = typeContextProps;
  const {
    onAddBlock,
    onDeleteBlock,
    onUpdateBlock,
    onAddBlockInsideSpoiler,
    onUpdateBlockInsideSpoiler,
    onDeleteBlockInsideSpoiler,
  } = blockActions;

  if (isInsideAccordion && block.type === 'accordion') return null;
  if (block._destroy) return null;

  const config = BLOCKS_CONFIG[block.type];
  if (!config) return null;

  const { Component, isSticky, withContentWrapper, getAnchorId, getCustomProps, getBlockProps } = config;
  if (!Component) return null;

  const handledWithContentWrapper = (() => {
    // used for remove wrapper inside preTravelInfo page builder
    if (isPreTravelInfo && block?.type === 'accordion') return false;

    return withContentWrapper as boolean;
  })();

  visibleBlockIndex.current = visibleBlockIndex.current + 1;

  const onAdd = onAddBlock?.(blockIndex);
  const onUpdate = debounce(onUpdateBlock?.(blockIndex), 300);
  const onDelete = onDeleteBlock?.(blockIndex);

  const payload = {
    blockIndex,
    isSticky,
    isPortal,
    isPortalItinerary,
    isMobileNavHidden,
    isItinerariesItem,
    isStickyWithShift: hasHeader || isPortal,
    onDelete,
    onUpdate,
    onAdd,
    onAddBlockInsideSpoiler,
    onUpdateBlockInsideSpoiler,
    onDeleteBlockInsideSpoiler,
    pageHeadlineRef: isSticky ? pageHeadlineRef : null,
  };

  const indexes = {
    pageIndex: pageIndex || 0,
    blockIndex,
  };
  const customProps = getCustomProps({ page: landingData, blockIndex: visibleBlockIndex.current });
  const blockProps: IGetBlockPropsResult = getBlockProps({
    blockIndex: visibleBlockIndex.current,
    disableFirstSectionNameRemoval,
  });

  let needToViewBlockerAnchor = isNextBlocked && !firstBlockerRendered.current;

  if (needToViewBlockerAnchor) {
    firstBlockerRendered.current = true;
  }

  return (
    <BlockWrapper
      key={block.id ?? block.originalId ?? blockIndex}
      withContentWrapper={handledWithContentWrapper}
      zeroStickyOffset={isSticky && visibleBlockIndex.current === 0}
      data={block}
      anchorId={getAnchorId({ ...block, ...payload })}
      indexes={indexes}
      isShownAddNewContent={
        blockAddingAvailable
          ? !isFalse(blockProps.isShownAddNewContent as boolean)
          : isTrue(blockProps.isShownAddNewContent as boolean)
      }
      isInsideAccordion={isInsideAccordion}
      {...payload}
      {...blockProps}
      isSticky={isSticky}
    >
      <BlockProvider blockId={blockId} pageHeaderRef={pageHeadlineRef}>
        {/* FIXME: remove ts-ignore */}
        {/* @ts-ignore */}
        <Component data={block} indexes={indexes} {...customProps} {...payload} />
        {needToViewBlockerAnchor && <div ref={blockerRef} />}
      </BlockProvider>
    </BlockWrapper>
  );
}

export { LandingBlock };
